import { useDraggable } from '@dnd-kit/core';
import { useTranslation, VisuallyHidden } from '@hbs-us/ui';
import { GripVertical } from 'lucide-react';

import type { DispatchJobDto } from './contractsMirror.js';

/**
 * The "Unscheduled" rail — the draggable source of assignable jobs (Active / On Hold). Each card is
 * a `useDraggable` activator that is ALSO a focusable `role="button"` (`tabIndex=0`) carrying a
 * `GripVertical` handle with a VisuallyHidden label, AND a non-drag "Schedule…" button (the
 * mandatory keyboard fallback — A11Y-01). Token-only — no hex.
 *
 * Dragging is the pointer/keyboard-sensor path; the "Schedule…" button is the explicit non-drag
 * path. Both ultimately POST `/api/v1/dispatch/assign` (the parent wires the handlers).
 */

function JobCard({
  job,
  onSchedule,
}: {
  job: DispatchJobDto;
  onSchedule: (job: DispatchJobDto) => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `job-${job.id}`,
    data: { jobId: job.id, jobName: job.jobName },
  });

  return (
    <li className={`rounded-md border border-border bg-card p-3 ${isDragging ? 'opacity-50' : ''}`}>
      <div className="flex items-start gap-2">
        {/* The drag activator: focusable, role=button, with a screen-reader-labelled grip. */}
        <button
          ref={setNodeRef}
          type="button"
          className="mt-0.5 cursor-grab rounded-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          {...listeners}
          {...attributes}
        >
          <GripVertical aria-hidden="true" className="h-4 w-4" />
          <VisuallyHidden>{t('dispatch:rail.dragHandle', { job: job.jobName })}</VisuallyHidden>
        </button>

        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-foreground text-sm">{job.jobName}</p>
          {job.clientName ? (
            <p className="truncate text-muted-foreground text-xs">{job.clientName}</p>
          ) : null}
          {/* Status carried as TEXT, never color alone (A11Y-01). */}
          <p className="mt-1 text-muted-foreground text-xs">{job.status}</p>
        </div>
      </div>

      {/* The MANDATORY non-drag fallback (A11Y-01): keyboard-operable scheduling. */}
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={() => onSchedule(job)}
          className="inline-flex h-8 items-center rounded-md bg-secondary px-3 font-medium text-secondary-foreground text-xs transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
        >
          {t('dispatch:rail.schedule')}
        </button>
      </div>
    </li>
  );
}

export function UnscheduledRail({
  jobs,
  onSchedule,
}: {
  jobs: DispatchJobDto[];
  onSchedule: (job: DispatchJobDto) => void;
}) {
  const { t } = useTranslation();

  return (
    <section aria-label={t('dispatch:rail.heading')} className="flex flex-col gap-2">
      <h2 className="font-display font-semibold text-foreground text-sm">
        {t('dispatch:rail.heading')}
      </h2>
      {jobs.length === 0 ? (
        <p className="rounded-md border border-border border-dashed bg-card p-4 text-center text-muted-foreground text-xs">
          {t('dispatch:rail.empty')}
        </p>
      ) : (
        <ul aria-label={t('dispatch:rail.listLabel')} className="flex flex-col gap-2">
          {jobs.map((job) => (
            <JobCard key={job.id} job={job} onSchedule={onSchedule} />
          ))}
        </ul>
      )}
    </section>
  );
}
